// @ts-strict-ignore
import { ActionType } from 'flow/store/engine/constants';
import type {
  Action,
  Attribute,
  Expression,
  ExpressionTerm,
  OutputObjectInAction,
} from 'flow/store/engine/types';

import { getActionBorderBottom } from 'flow/editor/styles';

import { MULTISELECT_PROPS } from '../../constants';
import { functionOptions } from '../../NodeEditorDrawer/constants';
import { getActionName, getActionTypeText, getNumericPathIndices } from '~/dashboard/views/workflows/service/actions';
import type { DropdownOption } from '../../NodeEditorDrawer/types';
import { RuleEditorDropdownMode } from '../../NodeEditorDrawer/types';
import { getProperty } from 'dot-prop';
import {
  createStringOfPathArray,
  getExpressionFunction,
  getIsExpressionGroupAwaitingOperands,
  getIsExpressionOperatorString,
} from '~/dashboard/views/workflows/store/engine/utils';
import { getErrorAttributeLabel, isErrorAttribute, isJsonAttribute } from 'flow/service/services';
import { insertIntoExpression } from 'flow/store/engine/update/expression';


/**
 * True when a group opened here could close around what follows. A min/max holds arguments rather
 * than a sequence, so bracketing part of one is never offered, however many follow.
 */
export function getHasItemsToGroupAfter({
  expressions,
  expressionPath,
  atGroupStart,
}: {
  expressions?: Expression;
  expressionPath?: string;
  atGroupStart?: boolean;
}) {
  if (!expressions?.length) return false;

  const indices = getNumericPathIndices(expressionPath);
  // an empty path sits before the first item, which is inside the function if the whole expression is one
  if (!indices?.length) return !getExpressionFunction(expressions);

  // at a group's opening the path names the group, not an item the caret follows
  if (atGroupStart) {
    const groupOnPath = getProperty(expressions, expressionPath);

    // the caret is inside this group, so what a parenthesis could close around is its own contents
    if (Array.isArray(groupOnPath)) {
      return !getExpressionFunction(groupOnPath)
        && !getIsExpressionGroupAwaitingOperands(groupOnPath);
    }
  }

  const parentGroup = (indices.length === 1
    ? expressions
    : getProperty(expressions, createStringOfPathArray(indices.slice(0, -1))));

  if (!Array.isArray(parentGroup)) return false;

  // a min/max group holds arguments, not a sequence, so bracketing part of it changes the result
  if (getExpressionFunction(parentGroup)) return false;

  return parentGroup.length > Number(indices.at(-1)) + 1;
}

/* Generates action or mode-specific styles in a style object to feed into the react-select style functions
 */
export function getDropdownTextStyles(action: Action, inlineSuggestion: boolean) {
  switch (action?.type) {
    case ActionType.SERVICE: {
      return {
        fontWeight: 'bold',
        color: 'var(--green-green-550, #3A795F)',
      };
    }
    case ActionType.OUTPUT:
    case ActionType.INPUT:
    case ActionType.MATRIX_MODEL: {
      return {
        fontWeight: 'bold',
        color: 'var(--cl-juicyBlue-500)',
      };
    }
    default:
      break;
  }
  return {
    color: inlineSuggestion ? 'var(--cl-yellow-400)' : 'var(--cl-text-default)',
    fontWeight: 'bold',
  };
}


/**
 * Green for a service or service attribute, blue for any other action, default for everything else.
 * A min/max name is pink, but that is applied in the option renderer so the rest of its label keeps
 * the default colour.
 */
export function getExpressionOptionColor(meta?: DropdownOption['meta']) {
  if (meta?.category === 'attribute') return 'var(--green-green-550, #3A795F)';

  if (meta?.category === 'action') {
    return meta.actionType === ActionType.SERVICE
      ? 'var(--green-green-550, #3A795F)'
      : 'var(--cl-juicyBlue-500)';
  }

  return undefined;
}

export function getMultiSelectProps(
  action: Action,
  mode,
  inlineSuggestion = false,
  isFunctionName = false,
) {
  return {
    ...MULTISELECT_PROPS,
    customStyleFunctions: {
      ...MULTISELECT_PROPS.customStyleFunctions,
      container: styles => ({
        ...styles,
        margin: '0',
      }),
      singleValue: styles => ({
        ...styles,
        borderTop: '2px solid transparent',
        borderBottom: getActionBorderBottom(action),
        fontFamily: 'var(--cl-font-family-mono)',
        fontSize: 'var(--cl-font-size-2)',
        ...(getDropdownTextStyles(action, inlineSuggestion)),
        // a min/max name is not an action, so it takes its colour after the action styles above
        ...(isFunctionName ? { color: 'var(--cl-pink-600)' } : {}),
      }),
      valueContainer: styles => ({
        ...styles,
        padding: mode === 'caretExpression' ? '0 1px' : '0 3px',
        caretColor: 'var(--cl-yellow-500)',
      }),
      // services and their attributes are green, other actions blue. A min/max name is coloured in the
      // option renderer instead, so the rest of its label keeps the default.
      option: (styles, state) => {
        const color = getExpressionOptionColor(state.data?.meta);
        return {
          ...MULTISELECT_PROPS.customStyleFunctions.option(styles, state),
          ...(color ? { color } : {}),
        };
      },
    },
  };
}

const isValidAvailableAction = (
  action: Action,
  publicId: string,
  outgoers: Set<string>,
) => (
  publicId !== action.publicId
  && action.type !== ActionType.OUTCOME
  && !outgoers.has(action.publicId)
);

export const getAvailableActionsAsDropdownOptions = (
  actions: Record<string, Action>,
  publicId: string,
  outgoers: Set<string>,
) => Object.entries(actions)
  .filter(([, action]) => isValidAvailableAction(action, publicId, outgoers))
  .map(([id, action]) => {
    const actionName = getActionName(action);
    return {
      label: `${getActionTypeText(action)}: ${actionName}`,
      value: id,
      meta: {
        category: 'action',
        shortenedLabel: `${actionName}${action.type === 'service' ? ': ' : ''}`,
        actionType: action.type,
      },
    } as DropdownOption;
  })
  .sort((a, b) => a.label.localeCompare(b.label));

export const getAvailableAttributesAsDropdownOptions = (
  allowedAttributes: { [attribute_id: number]: Attribute },
) => Object.entries(allowedAttributes)
  .filter(([, attribute]) => (!attribute.archivedAt && !isJsonAttribute(attribute)))
  .sort((a, b) => a[1].display_name.localeCompare(b[1].display_name))
  .map(([id, attribute]) => ({
    label: isErrorAttribute(attribute)
      ? getErrorAttributeLabel(attribute)
      : attribute.display_name,
    value: id,
    meta: {
      category: 'attribute',
      isMLA: attribute.is_multilevel,
      showAttributeWidget: isErrorAttribute(attribute),
      attribute,
    },
  } as DropdownOption));

export const getPreviousItemInExpressionScope = (
  {
    expressions, expressionPath, mode, output, atGroupStart,
  }: {
    expressions?: Expression;
    expressionPath?: string;
    mode: RuleEditorDropdownMode;
    output: OutputObjectInAction;
    atGroupStart?: boolean;
  },
) => {
  if (mode === RuleEditorDropdownMode.CARET_EXPRESSION) {
    // morphing caret is in first position, has no previous item
    if (expressionPath === '') return undefined;

    // the path names the group the caret is inside: a function's first argument has nothing before
    // it, while a plain group's first value is right there, so an operand would land beside it
    if (atGroupStart) {
      const groupOnPath = getProperty(output.expression, expressionPath);

      if (Array.isArray(groupOnPath)) {
        return getExpressionFunction(groupOnPath) ? undefined : groupOnPath;
      }
    }

    if (expressionPath) {
      // dropdown is in a position where it's empty (not created)
      const indices = getNumericPathIndices(expressionPath);

      // get previous item to generate relevant options for current item
      if (indices.length === 1) return expressions[Number(indices[0])];

      const pathWithoutLastIndex = indices.slice(0, -1)
        .reduce((result, indexValue) => `${result}[${indexValue}]`, '');
      const parentExpressionArrayOfTheExpressionOnTheLastIndex = getProperty(
        output.expression, pathWithoutLastIndex) as ExpressionTerm[];

      const itemIndex = Number(indices.at(-1));
      const groupFunction = getExpressionFunction(
        parentExpressionArrayOfTheExpressionOnTheLastIndex);
      const nextItem = parentExpressionArrayOfTheExpressionOnTheLastIndex[itemIndex + 1];

      // a min/max operator is drawn as the comma after this item, so the caret follows it
      return groupFunction && nextItem === groupFunction
        ? nextItem
        : parentExpressionArrayOfTheExpressionOnTheLastIndex[itemIndex];
    }
  }

  return expressions?.at(-1) || undefined;
};

/**
 * Where a newly created item will land, so a create's undo entry points at the item rather than at
 * the dropdown. Asks the function that does the placing instead of predicting it separately.
 */
export const getCreatedExpressionItemPath = ({
  existingExpression,
  path,
  insertion,
  value,
  atGroupStart,
}: {
  existingExpression: Expression;
  path: string;
  insertion: boolean;
  value?: ExpressionTerm | ExpressionTerm[];
  atGroupStart?: boolean;
}): string => insertIntoExpression({
  expression: existingExpression,
  path,
  // an operator never reaches a min/max group here, so a missing value stands in for the operand
  value: value ?? null,
  insertion,
  atGroupStart,
}).insertedPath;

export const canAddAvailableActionsToDropdown = (
  expressions: Expression,
  previousItemInExpressionsScope: ExpressionTerm | ExpressionTerm[],
) => {
  const isPreviousExpressionAnOperator = (
    typeof previousItemInExpressionsScope === 'string'
    && getIsExpressionOperatorString(previousItemInExpressionsScope)
  );

  const isPreviousItemAGroupAwaitingOperands = getIsExpressionGroupAwaitingOperands(
    previousItemInExpressionsScope as Expression);

  const isFirstItemInExpression = previousItemInExpressionsScope === undefined;
  const isExpressionEmpty = expressions.length <= 0;

  return (
    isPreviousExpressionAnOperator
    || isPreviousItemAGroupAwaitingOperands
    || isFirstItemInExpression
    || isExpressionEmpty
  );
};

type CreatableExpressionItem = 'function' | 'number' | 'string';

const getCreatableExpressionItem = ({
  typed,
  isFunction,
}: {
  typed: string;
  isFunction: boolean;
}): CreatableExpressionItem => {
  if (isFunction) return 'function';
  if (!isNaN(Number(typed))) return 'number';
  return 'string';
};

/**
 * The min/max option the typed text names, matching either the stored 'min' or the drawn "Min".
 * Any other spelling, "MIN" included, is ordinary text.
 */
export const getTypedFunctionOption = (typed: string) => functionOptions.find(
  option => typed === option.value || typed === option.meta?.shortenedLabel,
);

/**
 * The dropdown's offer to take typed text as a new expression item, e.g. "Add number: 5". A function
 * offers the name the editor draws, so `min` and `Min` both read "Add function: Min". The item names
 * double as the words in the offer, so a new one needs a clause in getCreatableExpressionItem only.
 */
export const formatCreatableExpressionItem = ({
  typed,
  functionOption,
}: {
  typed: string;
  functionOption?: DropdownOption;
}) => {
  const item = getCreatableExpressionItem({ typed, isFunction: Boolean(functionOption) });

  return `Add ${item}: ${functionOption?.meta?.shortenedLabel ?? typed}`;
};
